<template>
  <div class="audit-findings">
    <!-- Header -->
    <div class="bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex flex-col space-y-4">
          <a @click="$emit('back')" href="javascript:void(0)" class="flex items-center text-sm text-gray-500 hover:text-gray-700 self-start cursor-pointer">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to Farm Details
          </a>
          <h1 class="text-xl font-medium text-gray-900">Audit findings & Non-conformities for <span class="text-[#3A895F]">{{ farmName }}</span></h1>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 bg-white" style="border-left: none;">
      
      <!-- Farm Info -->
      <div class="mb-6">
        <div class="space-y-2">
          <p class="text-sm text-gray-500">Property: <span class="text-[#262F2A] font-bold">{{ farmName }} ({{ trustName }})</span></p>
          <p class="text-sm text-gray-500">PIC: <span class="text-[#262F2A] font-bold">{{ pic }}</span></p>
          <p class="text-sm text-gray-500">Audit Type: <span class="text-[#262F2A] font-bold">{{ auditType }}</span></p>
          <p class="text-sm text-gray-500">Date: <span class="text-[#262F2A] font-bold">{{ auditDate }}</span></p>
          <p class="text-sm text-gray-500">Auditor: <span class="text-[#262F2A] font-bold">{{ auditor }}</span></p>
        </div>
      </div>
      
      <!-- Description -->
      <div class="mb-8">
        <p class="text-sm text-gray-700">Based on integrated data from AUS-MEAT, LPA, and NLIS systems, plus on-farm verification, the following non-conformities and medium to major findings were identified:</p>
      </div>
      
      <!-- Major Findings Section -->
      <div class="mb-10">
        <h2 class="text-lg font-medium text-green-700 mb-4">Severity Major</h2>
        
        <div class="overflow-x-auto">
          <table class="min-w-full border-collapse">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 w-24">Due Date</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 w-48">Area / Module</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500">Description</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500">Evidence / Reference</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500">Corrective Action Required</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="finding in majorFindings" :key="finding.id" class="border-b border-gray-200">
                <td class="py-4 px-4 text-sm font-medium text-gray-900">{{ finding.dueDate }}</td>
                <td class="py-4 px-4 text-sm text-gray-900">{{ finding.area }}</td>
                <td class="py-4 px-4 text-sm text-gray-600">{{ finding.description }}</td>
                <td class="py-4 px-4 text-sm" v-html="finding.evidence"></td>
                <td class="py-4 px-4 text-sm text-gray-600">{{ finding.action }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Medium Findings Section -->
      <div class="mb-10">
        <h2 class="text-lg font-medium text-green-700 mb-4">Severity Medium</h2>
        
        <div class="overflow-x-auto">
          <table class="min-w-full border-collapse">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 w-24">Due Date</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500 w-48">Area / Module</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500">Description</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500">Evidence / Reference</th>
                <th class="py-3 px-4 text-left text-sm font-medium text-gray-500">Corrective Action Required</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="finding in mediumFindings" :key="finding.id" class="border-b border-gray-200">
                <td class="py-4 px-4 text-sm font-medium text-gray-900">{{ finding.dueDate }}</td>
                <td class="py-4 px-4 text-sm text-gray-900">{{ finding.area }}</td>
                <td class="py-4 px-4 text-sm text-gray-600">{{ finding.description }}</td>
                <td class="py-4 px-4 text-sm" v-html="finding.evidence"></td>
                <td class="py-4 px-4 text-sm text-gray-600">{{ finding.action }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- Observations Section -->
      <div class="mb-10">
        <h2 class="text-lg font-medium text-green-700 mb-4">Observations (Non-graded)</h2>
        
        <ul class="list-disc pl-5 text-sm text-gray-700 space-y-2">
          <li>Forms contain handwriting that is good condition, no evidence of misstatement or non-compliance.</li>
          <li>Biosecurity signage adequate but could be standardized at multiple entry points.</li>
          <li>Good cooperation from property manager during verification.</li>
        </ul>
      </div>
      
      <!-- Related Section -->
      <div>
        <h2 class="text-lg font-medium text-green-700 mb-4">Related</h2>
        
        <ul class="space-y-0 border-t border-gray-200">
          <li class="flex justify-between items-center py-4 border-b border-gray-200">
            <a href="#" style="color: #145E37" class="hover:underline">Create a call verification of corrective actions for open CARs</a>
            <div class="flex justify-end w-8">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.47917 8L6.16667 10.3125L7.22917 11.375L10.6042 8L7.22917 4.625L6.16667 5.6875L8.47917 8ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16ZM8 14.5C9.80556 14.5 11.3403 13.8681 12.6042 12.6042C13.8681 11.3403 14.5 9.80556 14.5 8C14.5 6.19444 13.8681 4.65972 12.6042 3.39583C11.3403 2.13194 9.80556 1.5 8 1.5C6.19444 1.5 4.65972 2.13194 3.39583 3.39583C2.13194 4.65972 1.5 6.19444 1.5 8C1.5 9.80556 2.13194 11.3403 3.39583 12.6042C4.65972 13.8681 6.19444 14.5 8 14.5Z" fill="#3A895F"/>
              </svg>
            </div>
          </li>
          <li class="flex justify-between items-center py-4 border-b border-gray-200">
            <a href="#" style="color: #145E37" class="hover:underline">Investigate the farm's wool supply chain</a>
            <div class="flex justify-end w-8">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.47917 8L6.16667 10.3125L7.22917 11.375L10.6042 8L7.22917 4.625L6.16667 5.6875L8.47917 8ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16ZM8 14.5C9.80556 14.5 11.3403 13.8681 12.6042 12.6042C13.8681 11.3403 14.5 9.80556 14.5 8C14.5 6.19444 13.8681 4.65972 12.6042 3.39583C11.3403 2.13194 9.80556 1.5 8 1.5C6.19444 1.5 4.65972 2.13194 3.39583 3.39583C2.13194 4.65972 1.5 6.19444 1.5 8C1.5 9.80556 2.13194 11.3403 3.39583 12.6042C4.65972 13.8681 6.19444 14.5 8 14.5Z" fill="#3A895F"/>
              </svg>
            </div>
          </li>
          <li class="flex justify-between items-center py-4 border-b border-gray-200">
            <a href="#" style="color: #145E37" class="hover:underline">Research the Central West Dubbo region's wool production</a>
            <div class="flex justify-end w-8">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.47917 8L6.16667 10.3125L7.22917 11.375L10.6042 8L7.22917 4.625L6.16667 5.6875L8.47917 8ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16ZM8 14.5C9.80556 14.5 11.3403 13.8681 12.6042 12.6042C13.8681 11.3403 14.5 9.80556 14.5 8C14.5 6.19444 13.8681 4.65972 12.6042 3.39583C11.3403 2.13194 9.80556 1.5 8 1.5C6.19444 1.5 4.65972 2.13194 3.39583 3.39583C2.13194 4.65972 1.5 6.19444 1.5 8C1.5 9.80556 2.13194 11.3403 3.39583 12.6042C4.65972 13.8681 6.19444 14.5 8 14.5Z" fill="#3A895F"/>
              </svg>
            </div>
          </li>
          <li class="flex justify-between items-center py-4 border-b border-gray-200">
            <a @click="goToPreAuditGuidance" href="javascript:void(0)" style="color: #145E37" class="hover:underline cursor-pointer">Help in preparation for the next audit</a>
            <div class="flex justify-end w-8">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.47917 8L6.16667 10.3125L7.22917 11.375L10.6042 8L7.22917 4.625L6.16667 5.6875L8.47917 8ZM8 16C6.90278 16 5.86806 15.7917 4.89583 15.375C3.92361 14.9583 3.07292 14.3854 2.34375 13.6562C1.61458 12.9271 1.04167 12.0764 0.625 11.1042C0.208333 10.1319 0 9.09722 0 8C0 6.88889 0.208333 5.85069 0.625 4.88542C1.04167 3.92014 1.61458 3.07292 2.34375 2.34375C3.07292 1.61458 3.92361 1.04167 4.89583 0.625C5.86806 0.208333 6.90278 0 8 0C9.11111 0 10.1493 0.208333 11.1146 0.625C12.0799 1.04167 12.9271 1.61458 13.6562 2.34375C14.3854 3.07292 14.9583 3.92014 15.375 4.88542C15.7917 5.85069 16 6.88889 16 8C16 9.09722 15.7917 10.1319 15.375 11.1042C14.9583 12.0764 14.3854 12.9271 13.6562 13.6562C12.9271 14.3854 12.0799 14.9583 11.1146 15.375C10.1493 15.7917 9.11111 16 8 16ZM8 14.5C9.80556 14.5 11.3403 13.8681 12.6042 12.6042C13.8681 11.3403 14.5 9.80556 14.5 8C14.5 6.19444 13.8681 4.65972 12.6042 3.39583C11.3403 2.13194 9.80556 1.5 8 1.5C6.19444 1.5 4.65972 2.13194 3.39583 3.39583C2.13194 4.65972 1.5 6.19444 1.5 8C1.5 9.80556 2.13194 11.3403 3.39583 12.6042C4.65972 13.8681 6.19444 14.5 8 14.5Z" fill="#3A895F"/>
              </svg>
            </div>
          </li>
        </ul>
      </div>
      
      <!-- Chat Input -->
      <div class="mt-8 border border-gray-200 rounded-lg p-4">
        <div class="flex items-center">
          <input type="text" placeholder="Ask a follow-up" class="flex-1 border-0 focus:ring-0 sm:text-sm" />
          <button class="ml-4 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
            <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
          </button>
          <button class="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
            <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clip-rule="evenodd" />
            </svg>
          </button>
          <button class="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 3a1 1 0 00-1 1v5H4a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V4a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AuditFindings',
  props: {
    farmName: {
      type: String,
      default: 'Ironbark Station'
    }
  },
  data() {
    return {
      pic: 'NSWHB2344',
      auditType: 'Routine LPA (Targeted Date-Optimised)',
      auditDate: '15 December 2023',
      auditor: 'A Williams (AUS-MEAT Accredited Auditor)',
      majorFindings: [
        {
          id: 1,
          dueDate: '15 Nov 2025',
          area: 'Biosecurity - Visitor Log & Entry Protocols',
          description: 'The property visitor log had no recorded entries for a three-month period (May-Aug 2025), no disinfection station maintained at entry.',
          evidence: 'Site inspection, LPA audit <a href="#" class="text-[#3A895F] hover:underline">photographic evidence</a>',
          action: 'Allocate staff to regularly maintain sanitizer at entry; re-train staff on sign-in requirements.'
        },
        {
          id: 2,
          dueDate: '15 Nov 2025',
          area: 'Animal Treatment Records',
          description: 'Incomplete recording of antibiotic treatments (oxytetracycline) administered in Mar 2025, missing withholding period documentation.',
          evidence: 'Vet report (A. Martinez, 17 Mar 2025), <a href="#" class="text-[#3A895F] hover:underline">missing entries</a>',
          action: 'Update treatment register with historical data, implement checklist for future entries.'
        }
      ],
      mediumFindings: [
        {
          id: 3,
          dueDate: '15 Nov 2025',
          area: 'Feed & Cropping Inputs',
          description: 'No supporting invoices or spray records for 2025 winter cropping program. Potential gap in residue traceability.',
          evidence: 'Farm records, supplier receipts unavailable',
          action: 'Collect and store supplier documentation, upload to LPA portal.'
        },
        {
          id: 4,
          dueDate: '15 Nov 2025',
          area: 'NLIS Data Integrity',
          description: 'Two outgoing livestock consignments (Apr and Jun 2025) recorded without confirmed receiving PICs in NLIS.',
          evidence: 'NLIS transaction report <a href="#" class="text-[#3A895F] hover:underline">check</a>',
          action: 'Verify transfers with recipients, correct records in NLIS database.'
        }
      ]
    }
  },
  methods: {
    goToPreAuditGuidance() {
      // Emit an event to the parent component to navigate to pre-audit guidance
      this.$emit('show-pre-audit-guidance');
    }
  },
  mounted() {
    // Scroll to top when component is mounted
    window.scrollTo(0, 0);
  },
  computed: {
    trustName() {
      // Farm owner mapping
      const farmOwnerMapping = {
        'Kookaburra Plains': 'Ash & Mia Robertson',
        'Bluegum Ridge': 'Bluegum Ag Unit Trust',
        'Banksia Vale': 'Lee & Carter Pty Ltd',
        'Eucalyptus Park': 'Euca Holdings Pty Ltd',
        'Ironbark Station': 'Nguyen Family Trust',
        'Redbank Hill': 'Redbank Hill Estates',
        'Rivergum Plains': 'Rivergum Pastures Pty Ltd',
        'Snowy View': 'Snowy View Holdings',
        'Wattle Creek': 'Wattle Creek Family Trust',
        'Waratah Downs': 'Waratah Pastoral Co.'
      };
      
      return farmOwnerMapping[this.farmName] || 'Unknown Owner';
    }
  }
}
</script>

<style scoped>
.audit-findings {
  min-height: 100vh;
  border-left: none !important;
}

.audit-findings * {
  border-left: none !important;
}
</style>
